import { Component, OnDestroy, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmpresaContextService } from '@core';
import { DropdownComponent, type DropdownOption } from '@shared';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-empresa-selector-global',
  standalone: true,
  imports: [CommonModule, DropdownComponent, TranslatePipe],
  templateUrl: './empresa-selector-global.component.html',
})
export class EmpresaSelectorGlobalComponent implements OnDestroy {
  readonly empresaContext = inject(EmpresaContextService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  private readonly rucVisibleEmpresaPrefixes = [
    this.normalizeName('PARTNER TECH'),
    this.normalizeName('KAIZEN TALENT'),
    this.normalizeName('LUGER ASOCIADOS'),
  ];

  disabled = false;
  disabledReason = '';

  private readonly routerSub: Subscription;
  private readonly langSub: Subscription;

  constructor() {
    this.updateDisabledFromRoute();
    // Asegura que el selector se re-renderice cuando el idioma cambie en ngx-translate.
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.cdr.markForCheck();
    });
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.updateDisabledFromRoute();
        this.cdr.markForCheck();
      });
  }

  private updateDisabledFromRoute(): void {
    const root = this.router.routerState.snapshot.root;
    const leaf = this.getLeafRoute(root);
    if (!leaf) {
      this.disabled = false;
      this.disabledReason = '';
      return;
    }
    const childHasFlag = leaf.data['globalSelectorDisabled'] === true;
    this.disabled = childHasFlag;
    if (this.disabled) {
      this.disabledReason = 'No se puede cambiar de empresa en este módulo.';
    } else {
      this.disabledReason = '';
    }
  }

  /** Obtiene la ruta hoja (más profunda) del árbol de rutas activas */
  private getLeafRoute(route: ActivatedRouteSnapshot | null): ActivatedRouteSnapshot | null {
    if (!route) return null;
    let leaf: ActivatedRouteSnapshot | null = route;
    while (leaf?.firstChild) {
      leaf = leaf.firstChild;
    }
    return leaf;
  }

  get displayEmpresaValue(): number | null {
    return this.empresaContext.selectedEmpresaId();
  }

  get empresaLabel(): string {
    const key = this.displayEmpresaValue == null ? 'header.empresa.label' : 'header.empresa.activeLabel';
    return this.translate.instant(key);
  }

  get empresaOptions(): DropdownOption<number | null>[] {
    const selectOption: DropdownOption<number | null> = {
      value: null,
      label: this.translate.instant('header.empresa.selectOption'),
    };

    const companyOptions = this.empresaContext.empresas().map((e) => ({
      value: e.empresaId,
      label: `${e.nombre}`,
    }));

    return [selectOption, ...companyOptions];
  }

  get showSelectedEmpresaRuc(): boolean {
    if (!this.isMantenimientoConceptosRoute()) {
      return false;
    }

    const selectedEmpresa = this.empresaContext.selectedEmpresa();
    if (!selectedEmpresa?.nombre) {
      return false;
    }

    const normalizedName = this.normalizeName(selectedEmpresa.nombre);
    return this.rucVisibleEmpresaPrefixes.some((prefix) => normalizedName.startsWith(prefix));
  }

  get selectedEmpresaRuc(): string {
    return this.empresaContext.selectedEmpresa()?.ruc ?? '';
  }

  onEmpresaChange(empresaId: number | null): void {
    if (empresaId == null) {
      this.empresaContext.setSelectedEmpresa(null);
      this.cdr.markForCheck();
      return;
    }
    this.empresaContext.setSelectedEmpresaById(empresaId);
  }

  ngOnDestroy(): void {
    this.routerSub.unsubscribe();
    this.langSub.unsubscribe();
  }

  private isMantenimientoConceptosRoute(): boolean {
    return this.router.url.includes('/configuracion-global/mantenimiento-conceptos');
  }

  private normalizeName(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Z0-9\s]/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }
}


